import { Component, inject, signal, effect, computed, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { NotificationApiService } from '../../services/notification.api.service';
import { NotificationGetModel } from '../../models/notification-get.model';
import { NotificationTypeIconPipe } from '../../pipes/notification-type-icon.pipe';
import { NotificationTypeIconBackgroundPipe } from '../../pipes/notification-type-icon-background.pipe';
import { NotificationDetailsModalComponent } from '../notification-details-modal/notification-details-modal.component';

@Component({
    selector: 'app-notification-dropdown',
    standalone: true,
    imports: [
        CommonModule, 
        TranslatePipe, 
        NotificationTypeIconPipe,
        NotificationTypeIconBackgroundPipe,
        NotificationDetailsModalComponent
    ],
    templateUrl: './notification-dropdown.component.html',
    styleUrl: './notification-dropdown.component.scss'
})
export class NotificationDropdownComponent {
    private readonly notificationApiService = inject(NotificationApiService);
    private readonly router = inject(Router);
    private readonly elementRef = inject(ElementRef);

    public isDropdownOpen = signal(false);
    public unreadNotifications = this.notificationApiService.unreadNotifications;
    public unreadCount = computed(() => this.unreadNotifications().length);
    public isLoading = signal(false);
    private openedByHover = signal(false);
    protected isDetailModalOpen = signal<boolean>(false);
    protected selectedNotification = signal<NotificationGetModel | null>(null);

    constructor() {
        this.loadUnreadNotifications();
        effect(() => {
            if (this.isDropdownOpen()) {
                this.loadUnreadNotifications();
            }
        });
    }

    public toggleDropdown = () => {
        this.isDropdownOpen.set(!this.isDropdownOpen());
    };

    public closeDropdown = () => {
        this.isDropdownOpen.set(false);
        this.openedByHover.set(false);
    };

    public onMouseEnter = () => {
        this.isDropdownOpen.set(true);
        this.openedByHover.set(true);
    };

    public onMouseLeave = () => {
        this.isDropdownOpen.set(false);
        this.openedByHover.set(false);
    };

    @HostListener('document:click', ['$event'])
    public onDocumentClick(event: MouseEvent): void {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.closeDropdown();
        }
    }

    private loadUnreadNotifications() {
        this.isLoading.set(true);
        this.notificationApiService.getUnreadNotifications().subscribe({
            next: () => {
                this.isLoading.set(false);
            },
            error: () => {
                this.isLoading.set(false);
            }
        });
    }

    public markAsRead(notificationId: String, event: Event) {
        event.stopPropagation();
        this.notificationApiService.markNotificationAsSeen(notificationId).subscribe();
    }

    public getTimeAgo(createdAt: Date): string {
        const date = new Date(createdAt);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMins < 1) return 'now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        return `${diffDays}d ago`;
    }

    public viewAllNotifications() {
        this.closeDropdown();
        this.router.navigate(['/notifications']);
    }

    protected openDetailModal(notification: NotificationGetModel): void {
        this.selectedNotification.set(notification);
        this.isDetailModalOpen.set(true);
    }

    protected onDetailModalNotificationUpdate(updatedNotification: NotificationGetModel): void {
        this.selectedNotification.set(updatedNotification);
        // State is automatically updated by the API service's tap operator
    }
}
